// src/components/ModalAdelantoChofer.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Button, Form, Row, Col, Alert, Spinner } from "react-bootstrap";
import { getChoferes } from "../helpers/rutaChoferes";
import { getCajas } from "../helpers/rutaCajas";
import { postAdelantoChofer } from "../helpers/rutaAdelantoChofer";

const todayLocalISO = () => {
  const d = new Date();
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const num = (v) => Number(v || 0) || 0;

const nowLocalDateTimeInput = () => {
  const d = new Date();
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16); // YYYY-MM-DDTHH:mm
};

const money = (n) =>
  num(n).toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const getChoferNombreBase = (c) => {
  if (!c) return "";
  return [c.chofer, c.nombre, c.apellido, c.razonsocial, c.descripcion]
    .filter(Boolean)
    .join(" ")
    .trim()
    .toUpperCase();
};

const getChoferLabel = (c) => {
  const nombre = getChoferNombreBase(c);
  return c?.codcho ? `${nombre} - ${c.codcho}` : nombre;
};

const normalizarChoferes = (resp) => {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp.choferes)) return resp.choferes;
  if (Array.isArray(resp.data?.choferes)) return resp.data.choferes;
  if (Array.isArray(resp.data)) return resp.data;
  return [];
};

const normalizarCajas = (resp) => {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp.cajas)) return resp.cajas;
  if (Array.isArray(resp.data?.cajas)) return resp.data.cajas;
  if (Array.isArray(resp.data)) return resp.data;
  return [];
};

const errorToText = (err) => {
  if (!err) return "Ocurrió un error";
  if (typeof err === "string") return err;
  if (typeof err?.error === "string") return err.error;
  if (typeof err?.msg === "string") return err.msg;
  if (typeof err?.message === "string") return err.message;
  try {
    return JSON.stringify(err);
  } catch {
    return "Ocurrió un error";
  }
};

export default function ModalAdelantoChofer({ show, setShow, onGuardado }) {
  const mountedRef = useRef(false);

  const [choferes, setChoferes] = useState([]);
  const [cajas, setCajas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const [formValues, setFormValues] = useState({
    chofer: "",
    fecha: nowLocalDateTimeInput(),
    mediopago: "EFECTIVO",
    caja: "",
    monto: "",
    observaciones: "",
  });

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const resetForm = () => {
    setError("");
    setFormValues({
      chofer: "",
      fecha: nowLocalDateTimeInput(),
      mediopago: "EFECTIVO",
      caja: "",
      monto: "",
      observaciones: "",
    });
  };

  const handleClose = () => {
    resetForm();
    setShow(false);
  };

  useEffect(() => {
    if (!show) return;

    let activo = true;

    const cargar = async () => {
      if (activo && mountedRef.current) {
        setCargando(true);
        setError("");
      }

      try {
        const [respChoferes, respCajas] = await Promise.all([
          getChoferes(),
          getCajas(),
        ]);

        if (activo && mountedRef.current) {
          setChoferes(normalizarChoferes(respChoferes));
          setCajas(
            normalizarCajas(respCajas)
              .filter((c) => c?.activo !== false)
              .sort((a, b) =>
                String(a?.descripcion || "").localeCompare(
                  String(b?.descripcion || ""),
                  "es",
                  { sensitivity: "base" }
                )
              )
          );
        }
      } catch (e) {
        if (activo && mountedRef.current) {
          setError("No se pudieron cargar choferes/cajas");
        }
      } finally {
        if (activo && mountedRef.current) {
          setCargando(false);
        }
      }
    };

    cargar();

    return () => {
      activo = false;
    };
  }, [show]);

  const handleChange = ({ target }) => {
    const { name, value } = target;
    if (error) setError("");

    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const cajaSeleccionada = useMemo(() => {
    return cajas.find((c) => String(c._id) === String(formValues.caja)) || null;
  }, [cajas, formValues.caja]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formValues.chofer) {
      setError("Seleccioná un chofer");
      return;
    }

    if (!formValues.fecha) {
      setError("Ingresá la fecha");
      return;
    }

    if (num(formValues.monto) <= 0) {
      setError("El monto debe ser mayor a 0");
      return;
    }

    if (formValues.mediopago === "EFECTIVO" && !formValues.caja) {
      setError("Seleccioná una caja");
      return;
    }

    if (
      formValues.mediopago === "EFECTIVO" &&
      num(cajaSeleccionada?.saldoActual) < num(formValues.monto)
    ) {
      setError(`Saldo insuficiente. Disponible: $ ${money(cajaSeleccionada?.saldoActual)}`);
      return;
    }

    setGuardando(true);
    setError("");

    try {
      const resp = await postAdelantoChofer({
        chofer: formValues.chofer,
        fecha: formValues.fecha ? new Date(formValues.fecha).toISOString() : null,
        monto: num(formValues.monto),
        mediopago: formValues.mediopago,
        caja: formValues.mediopago === "EFECTIVO" ? formValues.caja : null,
        observaciones: formValues.observaciones,
      });

      if (resp?.ok === false) {
        setError(errorToText(resp));
        return;
      }

      if (onGuardado) await onGuardado(resp);
      handleClose();
    } catch (e) {
      setError(errorToText(e?.response?.data || e));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered backdrop="static">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Adelanto a chofer</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Chofer</Form.Label>
                <Form.Select
                  name="chofer"
                  value={formValues.chofer}
                  onChange={handleChange}
                  disabled={cargando || guardando}
                >
                  <option value="">Seleccione chofer</option>
                  {choferes
                    .filter((c) => c?.activo !== false)
                    .sort((a, b) =>
                      getChoferNombreBase(a).localeCompare(getChoferNombreBase(b), "es", {
                        sensitivity: "base",
                      })
                    )
                    .map((c) => (
                      <option key={c._id} value={c._id}>
                        {getChoferLabel(c)}
                      </option>
                    ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Fecha</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    name="fecha"
                    value={formValues.fecha}
                    onChange={handleChange}
                    disabled={guardando}
                  />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Monto</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="0.01"
                  name="monto"
                  value={formValues.monto}
                  onChange={handleChange}
                  disabled={guardando}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Medio de pago</Form.Label>
                <Form.Select
                  name="mediopago"
                  value={formValues.mediopago}
                  onChange={handleChange}
                  disabled={guardando}
                >
                  <option value="EFECTIVO">EFECTIVO</option>
                  <option value="TRANSFERENCIA">TRANSFERENCIA</option>
                  <option value="CHEQUE">CHEQUE</option>
                  <option value="OTRO">OTRO</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Observaciones</Form.Label>
                <Form.Control
                  type="text"
                  name="observaciones"
                  value={formValues.observaciones}
                  onChange={handleChange}
                  disabled={guardando}
                />
              </Form.Group>
            </Col>
          </Row>

          {formValues.mediopago === "EFECTIVO" && (
            <Row className="mb-3">
              <Col md={7}>
                <Form.Group>
                  <Form.Label>Caja</Form.Label>
                  <Form.Select
                    name="caja"
                    value={formValues.caja}
                    onChange={handleChange}
                    disabled={guardando}
                  >
                    <option value="">Seleccione caja</option>
                    {cajas.map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.descripcion} - Saldo $ {money(c.saldoActual)}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={5}>
                <div className="border rounded p-3 bg-light h-100">
                  <strong>Saldo disponible</strong>
                  <div
                    style={{
                      fontSize: 22,
                      fontWeight: 700,
                      color:
                        num(cajaSeleccionada?.saldoActual) >= num(formValues.monto)
                          ? "#198754"
                          : "#dc3545",
                    }}
                  >
                    $ {money(cajaSeleccionada?.saldoActual)}
                  </div>
                </div>
              </Col>
            </Row>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={guardando}>
            Cancelar
          </Button>
          <Button variant="success" type="submit" disabled={guardando}>
            {guardando ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Guardando
              </>
            ) : (
              "Guardar adelanto"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}